import React, { useState } from 'react';
import { Star, Palette, Target, BarChart3, Plug, Check, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const STEPS = [
  {
    icon: Star,
    title: 'Bienvenido a Pro',
    description: 'Tu cuenta ya tiene acceso a herramientas nuevas. Antes de seguir, te mostramos rápidamente qué cambia.',
    bullets: [
      'Perfiles y personalización',
      'Leads y seguimiento',
      'Analíticas y campañas',
      'Herramientas avanzadas',
    ],
  },
  {
    icon: Palette,
    title: 'Perfiles y personalización',
    description: 'Ahora puedes crear hasta 3 perfiles digitales en vez de 1, personalizar temas y fondos, y tu perfil ya no muestra la marca de agua de Contact Network.',
    bullets: [
      'Hasta 3 perfiles digitales',
      'Personalización avanzada de apariencia',
      'Sin marca de agua',
    ],
    tabId: 'appearance',
  },
  {
    icon: Target,
    title: 'Leads y seguimiento',
    description: 'Cada contacto que capturas ahora cuenta como lead ilimitado, y puedes ver quién escaneó tu perfil. El seguimiento automático avisa cuando alguien no ha respondido.',
    bullets: [
      'Leads ilimitados por mes',
      'Identificación de quién te escaneó',
      'Seguimiento automático por correo',
    ],
    tabId: 'leads',
  },
  {
    icon: BarChart3,
    title: 'Analíticas y campañas',
    description: 'Accede a analíticas avanzadas de tu perfil y agrupa tus leads por evento o campaña para saber de dónde vienen.',
    bullets: [
      'Analíticas avanzadas',
      'Atribución de leads por campaña',
    ],
    tabId: 'analytics',
  },
  {
    icon: Plug,
    title: 'Herramientas avanzadas',
    description: 'Conecta tus herramientas favoritas, usa la API y Webhooks para automatizar procesos, y exporta o cotiza cuando lo necesites.',
    bullets: [
      'Integraciones (Google Sheets y más)',
      'Acceso a la API y Webhooks',
      'Cotizaciones y exportación de datos',
    ],
    tabId: 'integrations',
  },
  {
    icon: Check,
    title: 'Todo listo',
    description: 'Ya conoces las herramientas principales de tu plan Pro. Puedes empezar a usarlas ahora mismo desde el panel.',
    bullets: [],
  },
];

/* One-time guided tour rendered from App.js right after a Pro user's
   plan loads (see App.js's showProTour effect) — deliberately not on
   SubscriptionSuccessPage.js, since that page only polls for MercadoPago
   confirmation for about 10 seconds; if the payment confirms after that
   window, is_pro would never flip true while the user is still on that
   page, and the tour would never show at all. Checking it here instead
   means it reliably shows the first time a Pro plan is detected, however
   that happened. Marked as seen in localStorage the moment it's
   dismissed or completed (App.js's dismissProTour), following the same
   {feature}_onboarding_seen_{userId} convention already used in
   LeadsPanel.js — never shown again after that, by design, with no
   revisit entry point elsewhere in the app.

   "Ver ahora" calls onViewTab to switch the dashboard's active tab
   directly (no page navigation involved, since this already lives
   inside the dashboard) and minimizes into a small resume pill instead
   of ending the tour, so looking at a section doesn't cost the rest of
   the walkthrough. The pill sits bottom-left rather than bottom-right,
   since ChatBot.js's floating button and chat window both live at
   bottom-right on every page, including the dashboard. */
const stepT = (t, i, field, fallback) => t(`proTour.step${i}.${field}`, fallback);
const stepBulletT = (t, i, j, fallback) => t(`proTour.step${i}.bullet${j}`, fallback);

const ProOnboardingTour = ({ onFinish, onViewTab }) => {
  const { t } = useLanguage();
  const [stepIndex, setStepIndex] = useState(0);
  const [minimized, setMinimized] = useState(false);

  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;
  const Icon = step.icon;

  const handleNext = () => {
    if (isLast) {
      onFinish();
      return;
    }
    setStepIndex((i) => i + 1);
  };

  const handleBack = () => {
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const handleViewNow = () => {
    if (step.tabId) {
      onViewTab(step.tabId);
    }
    setMinimized(true);
  };

  const handleSkip = () => {
    onFinish();
  };

  return (
    <>
      {minimized ? (
        <div className="pot-resume-pill">
          <button className="pot-resume-pill-btn" onClick={() => setMinimized(false)}>
            {t('proTour.continueGuide', `Continuar guía · Paso ${stepIndex + 1} de ${STEPS.length}`).replace('{step}', stepIndex + 1).replace('{total}', STEPS.length)}
          </button>
          <button className="pot-resume-pill-close" onClick={handleSkip} aria-label={t('proTour.skipGuide', 'Saltar guía')}>
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      ) : (
        <div className="pot-overlay">
          <div className="pot-modal">
            <button className="pot-skip" onClick={handleSkip}>{t('proTour.skipGuide', 'Saltar guía')}</button>

            <div className="pot-progress">
              {STEPS.map((_, i) => (
                <span key={i} className={`pot-dot${i === stepIndex ? ' pot-dot--active' : ''}`} />
              ))}
            </div>

            <div className="pot-icon-wrap">
              <Icon size={26} strokeWidth={1.75} />
            </div>

            <h2 className="pot-title">{stepT(t, stepIndex, 'title', step.title)}</h2>
            <p className="pot-desc">{stepT(t, stepIndex, 'description', step.description)}</p>

            {step.bullets.length > 0 && (
              <ul className="pot-bullets">
                {step.bullets.map((bullet, i) => (
                  <li key={i} className="pot-bullet">
                    <span className="pot-bullet-dot" />
                    <span>{stepBulletT(t, stepIndex, i, bullet)}</span>
                  </li>
                ))}
              </ul>
            )}

            {(stepIndex > 0 || step.tabId) && (
              <div className="pot-actions-row">
                {stepIndex > 0 && (
                  <button className="pot-btn-back" onClick={handleBack}>{t('proTour.back', 'Atrás')}</button>
                )}
                {step.tabId && (
                  <button className="pot-btn-secondary" onClick={handleViewNow}>{t('proTour.viewNow', 'Ver ahora')}</button>
                )}
              </div>
            )}

            <button className="pot-btn-primary" onClick={handleNext}>
              {isLast ? t('proTour.startUsingPro', 'Empezar a usar Pro') : t('proTour.next', 'Siguiente')}
            </button>
          </div>
        </div>
      )}
      <style>{TOUR_CSS}</style>
    </>
  );
};

const TOUR_CSS = `
.pot-overlay{position:fixed;inset:0;z-index:400;background:rgba(0,0,0,.5);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;padding:20px;animation:potFade .2s ease}
@keyframes potFade{from{opacity:0}to{opacity:1}}
@keyframes potSlide{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}
.pot-modal{position:relative;background:#fff;border-radius:16px;max-width:440px;width:100%;max-height:90vh;overflow-y:auto;padding:40px 36px 32px;box-shadow:0 30px 80px rgba(0,0,0,.35);font-family:'Inter',system-ui,sans-serif;text-align:center;animation:potSlide .25s cubic-bezier(.16,1,.3,1)}
.pot-skip{position:absolute;top:20px;right:20px;font-size:13px;color:#6e6e73;background:none;border:none;cursor:pointer;font-family:inherit}
.pot-skip:hover{color:#000;text-decoration:underline}
.pot-progress{display:flex;justify-content:center;gap:6px;margin-bottom:24px}
.pot-dot{width:6px;height:6px;border-radius:50%;background:#E5E5E5;transition:all .2s}
.pot-dot--active{background:#004AAD;width:18px;border-radius:3px}
.pot-icon-wrap{width:56px;height:56px;border-radius:14px;background:#EFF4FC;display:flex;align-items:center;justify-content:center;margin:0 auto 20px;color:#004AAD}
.pot-title{font-size:22px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;margin:0 0 10px}
.pot-desc{font-size:14.5px;color:#4a4a4a;line-height:1.55;margin:0 0 20px}
.pot-bullets{list-style:none;padding:0;margin:0 0 28px;text-align:left;display:flex;flex-direction:column;gap:10px}
.pot-bullet{display:flex;align-items:flex-start;gap:10px;font-size:13.5px;color:#1d1d1f}
.pot-bullet-dot{width:5px;height:5px;border-radius:50%;background:#004AAD;margin-top:6px;flex-shrink:0}
.pot-actions-row{display:flex;justify-content:space-between;gap:10px;margin-bottom:12px}
.pot-btn-primary{width:100%;height:46px;border-radius:8px;background:#004AAD;color:#fff;border:none;font-family:inherit;font-size:14.5px;font-weight:600;cursor:pointer;transition:opacity .15s}
.pot-btn-primary:hover{opacity:.9}
.pot-btn-secondary{height:44px;padding:0 18px;border-radius:8px;background:#fff;color:#004AAD;border:1.5px solid rgba(0,74,173,.25);font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;transition:background .15s}
.pot-btn-secondary:hover{background:#F5F8FD}
.pot-btn-back{height:44px;padding:0 16px;border-radius:8px;background:#fff;color:#6e6e73;border:1px solid #E5E5E5;font-family:inherit;font-size:14px;font-weight:500;cursor:pointer}
.pot-btn-back:hover{background:#f7f7f7}
.pot-resume-pill{position:fixed;bottom:24px;left:24px;z-index:400;display:flex;align-items:stretch;background:#004AAD;border-radius:100px;box-shadow:0 8px 24px rgba(0,74,173,.35);overflow:hidden}
.pot-resume-pill-btn{background:none;border:none;color:#fff;font-family:'Inter',system-ui,sans-serif;font-size:13.5px;font-weight:600;cursor:pointer;padding:12px 8px 12px 20px;transition:opacity .15s}
.pot-resume-pill-btn:hover{opacity:.9}
.pot-resume-pill-close{background:none;border:none;color:rgba(255,255,255,.75);cursor:pointer;padding:12px 16px 12px 8px;display:flex;align-items:center;transition:color .15s}
.pot-resume-pill-close:hover{color:#fff}

@media(max-width:520px){
  .pot-modal{padding:32px 22px 24px;border-radius:14px}
}
`;

export default ProOnboardingTour;
